import { useEffect, useState } from "react";
import { axios, API, logError } from "../lib/api";

const ANON_ID_KEY = "toycycle_anon_id";

/** Stable anonymous-visitor id stored once in localStorage. Combined with
 *  the user.id (when logged in) inside the backend, this gives us a sticky
 *  per-browser subject id for unbiased A/B assignment.
 *
 *  Security note: this is a random opaque string — NOT a token, NOT PII.
 *  It's the anonymous-cookie equivalent for analytics. localStorage is the
 *  right place: we want it to persist across tabs/sessions so the same
 *  visitor stays in the same variant. No XSS exposure beyond what an
 *  attacker already gets from window.location etc. */
const getAnonId = () => {
  if (typeof window === "undefined") return "anon-ssr";
  let id = localStorage.getItem(ANON_ID_KEY);
  if (!id) {
    id = `a-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    localStorage.setItem(ANON_ID_KEY, id);
  }
  return id;
};

/**
 * useExperiment — fetch the variant for a given experiment key.
 *
 * Returns `{ variant, payload, loading, track }`. The variant is sticky per
 * browser so the same user always sees the same copy/colour/CTA across
 * sessions. `track(event)` logs a goal conversion against the active variant.
 *
 * Safe defaults: if the request fails or the experiment is unknown, the
 * caller gets `{variant: "control", payload: fallback}` so the UI never
 * breaks — you always render *something*.
 *
 * @param {string} key             experiment key registered in /experiments seed
 * @param {object} fallback        payload to use when no experiment exists
 */
export function useExperiment(key, fallback = {}) {
  const [state, setState] = useState({
    variant: "control",
    payload: fallback,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    const anonId = getAnonId();
    axios
      .get(`${API}/experiments/assignment`, { params: { key, anon_id: anonId } })
      .then((r) => {
        if (cancelled) return;
        setState({
          variant: r.data.variant,
          payload: { ...fallback, ...(r.data.payload || {}) },
          loading: false,
        });
      })
      .catch((err) => {
        logError(`useExperiment(${key})`, err);
        if (cancelled) return;
        setState({ variant: "control", payload: fallback, loading: false });
      });
    return () => { cancelled = true; };
    // fallback intentionally excluded — it's an inline object every render,
    // and we want the request to fire exactly once per `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const track = (event) => {
    const anonId = getAnonId();
    axios
      .post(`${API}/experiments/track`, { key, event, anon_id: anonId })
      .catch((err) => logError(`useExperiment.track(${key},${event})`, err));
  };

  return { ...state, track };
}
